import { Trans, useTranslation } from 'react-i18next';

import { Select } from '@actual-app/components/select';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import { Boton } from '#components/mobile/ui/Boton';
import { CampoImporte } from '#components/mobile/ui/CampoImporte';
import { Segmentado } from '#components/mobile/ui/Segmentado';
import { color, texto } from '#components/mobile/ui/tokens';
import { useFormat } from '#hooks/useFormat';
import { useLocale } from '#hooks/useLocale';

import { CONFIG_VACIA, conTipo, sueldoDeVerdad } from './nomina';
import type { ConfigNomina, DiaCobro, PagaExtra, TipoPagas } from './nomina';
import { PropuestaNomina } from './PropuestaNomina';
import { mayuscula, nombreMes, nombrePaga } from './textos';
import { useConfigNomina, useNominaDeducida } from './useNomina';

/** Segmento que corresponde a la configuración (o ninguno si es a medida). */
function segmento(config: ConfigNomina): TipoPagas | '' {
  if (config.tipo === 'prorrateadas') {
    return 'prorrateadas';
  }
  switch (config.pagas.length) {
    case 0:
      return '12';
    case 2:
      return '14';
    case 3:
      return '15';
    default:
      return '';
  }
}

/** Tipo que se guarda al añadir o quitar pagas a mano. */
function tipoPorPagas(n: number): TipoPagas {
  return n === 0 ? '12' : n === 2 ? '14' : n >= 3 ? '15' : '14';
}

/**
 * Ajustes → «Tu nómina»: cuántas pagas, la nómina neta, el día de cobro y
 * cada paga extra (mes, día e importe). Se guarda en la pref sincronizada
 * `nomina` al cambiar cada cosa. Si aún no hay nada guardado, Rumbo propone
 * lo que ha deducido de los ingresos del último año. Tocar algo a mano apaga
 * la puesta al día automática (`auto`).
 */
export function NominaSettings() {
  const { t } = useTranslation();
  const format = useFormat();
  const locale = useLocale();
  const { config: guardada, guardar: guardarConfig } = useConfigNomina();
  const deducida = useNominaDeducida(guardada == null);
  const guardar = (nueva: ConfigNomina) =>
    guardarConfig({ ...nueva, auto: false });
  const config = guardada ?? CONFIG_VACIA;

  const opcionesDia: [DiaCobro, string][] = [
    ['ultimo', t('Last day of the month')],
    ...Array.from(
      { length: 31 },
      (_, i) => [i + 1, String(i + 1)] as [DiaCobro, string],
    ),
  ];
  const opcionesMes: [number, string][] = Array.from({ length: 12 }, (_, i) => [
    i + 1,
    nombreMes(i + 1, locale),
  ]);

  const cambiarPaga = (id: string, cambio: Partial<PagaExtra>) =>
    guardar({
      ...config,
      pagas: config.pagas.map(p => (p.id === id ? { ...p, ...cambio } : p)),
    });

  const quitarPaga = (id: string) => {
    const pagas = config.pagas.filter(p => p.id !== id);
    guardar({ ...config, pagas, tipo: tipoPorPagas(pagas.length) });
  };

  const anadirPaga = () => {
    const usados = new Set(config.pagas.map(p => p.mes));
    const mes = [3, 9, 4, 7, 1].find(m => !usados.has(m)) ?? 1;
    let n = config.pagas.length + 1;
    while (config.pagas.some(p => p.id === `extra-${n}`)) {
      n++;
    }
    const pagas = [
      ...config.pagas,
      {
        id: `extra-${n}`,
        nombre: '',
        mes,
        dia: 'ultimo' as const,
        importe: null,
      },
    ];
    guardar({ ...config, pagas, tipo: tipoPorPagas(pagas.length) });
  };

  return (
    <View style={{ gap: 14 }} data-testid="nomina-ajustes">
      {guardada == null && deducida && (
        <PropuestaNomina
          deducida={deducida}
          onConfirmar={guardarConfig}
          onCorregir={guardarConfig}
        />
      )}
      {guardada?.auto && (
        <Text
          data-testid="nomina-auto"
          style={{ ...texto.pequeno, color: color.fg3 }}
        >
          <Trans>
            Rumbo works this out from your income and keeps it up to date. If
            you change anything here, it stays as you leave it.
          </Trans>
        </Text>
      )}

      <Text style={{ ...texto.secundario, color: color.fg2 }}>
        <Trans>
          So Rumbo knows when extra money arrives and helps you decide what it
          will do before it vanishes.
        </Trans>
      </Text>

      <Segmentado
        etiqueta={t('How many payments do you get?')}
        testId="nomina-tipo"
        estirar
        valor={segmento(config)}
        onChange={tipo => tipo && guardar(conTipo(config, tipo))}
        opciones={[
          { valor: '12', texto: '12' },
          { valor: '14', texto: '14' },
          { valor: '15', texto: '15' },
          { valor: 'prorrateadas', texto: t('Prorated') },
        ]}
      />
      {config.tipo === 'prorrateadas' && (
        <Text style={{ ...texto.pequeno, color: color.fg3 }}>
          <Trans>
            Prorated means the extra payments already come inside each monthly
            payslip, so there are no extra months.
          </Trans>
        </Text>
      )}

      <View style={{ borderRadius: 16, overflow: 'hidden' }}>
        <CampoImporte
          etiqueta={t('Net monthly pay')}
          valor={config.neta}
          placeholder={format(0, 'financial')}
          onCambiar={centimos => guardar({ ...config, neta: centimos ?? 0 })}
          data-testid="nomina-neta"
        />
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            padding: '6px 14px',
            backgroundColor: color.surface,
          }}
        >
          <Text style={{ ...texto.cuerpo, color: color.fg }}>
            <Trans>Payday</Trans>
          </Text>
          <Select
            value={config.dia}
            options={opcionesDia}
            onChange={dia => guardar({ ...config, dia })}
          />
        </View>
      </View>

      {config.pagas.length > 0 && (
        <View style={{ gap: 8 }}>
          <Text style={{ ...texto.etiqueta, color: color.fg2 }}>
            <Trans>Extra payments</Trans>
          </Text>
          {config.pagas.map(paga => (
            <View
              key={paga.id}
              data-testid={`nomina-paga-${paga.id}`}
              style={{
                gap: 8,
                padding: 12,
                borderRadius: 16,
                backgroundColor: color.surface,
              }}
            >
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Text style={{ ...texto.cuerpo, fontWeight: 700 }}>
                  {mayuscula(nombrePaga(t, paga, locale))}
                </Text>
                <Boton
                  variante="fantasma"
                  onPress={() => quitarPaga(paga.id)}
                  aria-label={t('Remove {{name}}', {
                    name: nombrePaga(t, paga, locale),
                  })}
                >
                  <Trans>Remove</Trans>
                </Boton>
              </View>
              <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
                <Select
                  value={paga.mes}
                  options={opcionesMes}
                  onChange={mes => cambiarPaga(paga.id, { mes })}
                />
                <Select
                  value={paga.dia}
                  options={opcionesDia}
                  onChange={dia => cambiarPaga(paga.id, { dia })}
                />
              </View>
              <ImportePaga
                paga={paga}
                onCambiar={importe => cambiarPaga(paga.id, { importe })}
              />
            </View>
          ))}
        </View>
      )}
      {config.tipo !== 'prorrateadas' && (
        <Boton variante="fantasma" onPress={anadirPaga}>
          <Trans>Add another payment (profit share, bonus…)</Trans>
        </Boton>
      )}

      {config.neta > 0 && config.pagas.length > 0 && (
        <Text
          style={{ ...texto.secundario, color: color.fg2 }}
          data-testid="nomina-sueldo-verdad"
        >
          {t('Your real monthly pay: {{amount}} (the year divided by 12)', {
            amount: format(sueldoDeVerdad(config), 'financial'),
          })}
        </Text>
      )}
    </View>
  );
}

function ImportePaga({
  paga,
  onCambiar,
}: {
  paga: PagaExtra;
  onCambiar: (importe: number | null) => void;
}) {
  const { t } = useTranslation();
  return (
    <CampoImporte
      etiqueta={t('Amount')}
      valor={paga.importe}
      opcional
      placeholder={t('Same as monthly pay')}
      onCambiar={onCambiar}
    />
  );
}
